import { apiFetch } from "../lib/apiFetch";
import { useState, useEffect } from "react";
import {
  Save,
  BrainCircuit,
  MessageCircle,
  AlertCircle,
  Loader2,
  Link,
  Users,
  Clock,
  Flame,
  CreditCard,
  HelpCircle,
  Download,
  Smartphone,
  Apple,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface KBData {
  organizationName: string;
  businessDescription: string;
  qualificationQuestions: string;
  appDownloadLink: string;
  iosAppLink: string;
  androidAppLink: string;
  paymentLink: string;
  instructorProfiles: string;
  classTimings: string;
  streakSystemInfo: string;
  pricingPlans: string;
  faqAnswers: string;
  trialDays: number;
}

const EMPTY_KB: KBData = {
  organizationName: "",
  businessDescription: "",
  qualificationQuestions: "",
  appDownloadLink: "",
  iosAppLink: "",
  androidAppLink: "",
  paymentLink: "",
  instructorProfiles: "",
  classTimings: "",
  streakSystemInfo: "",
  pricingPlans: "",
  faqAnswers: "",
  trialDays: 7,
};

const YOGAIN_DEFAULTS: Partial<KBData> = {
  organizationName: "YogAIn Wellness",
  businessDescription:
    "YogAIn is an AI-powered yoga wellness app that provides personalized yoga sessions, live classes with expert instructors, and streak-based motivation to help users build a consistent yoga practice. Available on iOS and Android.",
  qualificationQuestions:
    "1. Have you downloaded the YogAIn app?\n2. What is your yoga experience level?\n3. What are your wellness goals?\n4. What time of day do you prefer to practice?\n5. Are you interested in continuing after the trial?",
  streakSystemInfo:
    "The streak system tracks consecutive days of practice. Each day you complete at least one session, your streak increases. Streaks unlock badges and special content. Missing a day resets your streak. Users can use a Streak Freeze (1 per week) to protect their streak.",
  faqAnswers:
    "Q: How do I download the app?\nA: Download YogAIn from the App Store (iOS) or Play Store (Android). Search for 'YogAIn Wellness'.\n\nQ: What is a streak?\nA: A streak counts how many consecutive days you practice. It resets if you skip a day, but you can use a Streak Freeze once a week.\n\nQ: How do I find my instructor?\nA: Open the app → Browse → Instructors.\n\nQ: When are live classes?\nA: Live classes run every day. Morning batch at 7 AM, evening batch at 7 PM IST.\n\nQ: What happens after my trial?\nA: You can continue with a paid plan. Your progress and streak are preserved.",
};

type Section = "app" | "instructors" | "timings" | "streak" | "pricing" | "faq" | "general";

const sections: { key: Section; label: string; icon: React.ElementType; color: string }[] = [
  { key: "general", label: "General Info", icon: BrainCircuit, color: "text-indigo-600" },
  { key: "app", label: "App Links", icon: Link, color: "text-green-600" },
  { key: "instructors", label: "Instructors", icon: Users, color: "text-blue-600" },
  { key: "timings", label: "Class Timings", icon: Clock, color: "text-amber-600" },
  { key: "streak", label: "Streak System", icon: Flame, color: "text-orange-600" },
  { key: "pricing", label: "Pricing Plans", icon: CreditCard, color: "text-emerald-600" },
  { key: "faq", label: "FAQ Answers", icon: HelpCircle, color: "text-purple-600" },
];

export function AIKnowledgeBase() {
  const [isSimulating, setIsSimulating] = useState(false);
  const [simInput, setSimInput] = useState("");
  const [simMessages, setSimMessages] = useState<{ role: "user" | "ai"; text: string }[]>([]);
  const [activeSection, setActiveSection] = useState<Section>("general");
  const [kbData, setKbData] = useState<KBData>(EMPTY_KB);
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const queryClient = useQueryClient();

  useEffect(() => {
    fetch("/api/whatsapp/owner")
      .then((r) => r.json())
      .then((d) => { if (d.ownerId) setOwnerId(d.ownerId); })
      .catch(() => {});
  }, []);

  const { data: remoteKb, isLoading } = useQuery({
    queryKey: ["knowledgeBase", ownerId],
    queryFn: async () => {
      const res = await apiFetch(`/api/knowledge-base?ownerId=${ownerId}`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!ownerId,
  });

  useEffect(() => {
    if (remoteKb) {
      setKbData({
        organizationName: remoteKb.organizationName || "",
        businessDescription: remoteKb.businessDescription || "",
        qualificationQuestions: remoteKb.qualificationQuestions || "",
        appDownloadLink: remoteKb.appDownloadLink || "",
        iosAppLink: remoteKb.iosAppLink || "",
        androidAppLink: remoteKb.androidAppLink || "",
        paymentLink: remoteKb.paymentLink || "",
        instructorProfiles: remoteKb.instructorProfiles || "",
        classTimings: remoteKb.classTimings || "",
        streakSystemInfo: remoteKb.streakSystemInfo || "",
        pricingPlans: remoteKb.pricingPlans || "",
        faqAnswers: remoteKb.faqAnswers || "",
        trialDays: remoteKb.trialDays ?? 7,
      });
    }
  }, [remoteKb]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch("/api/knowledge-base", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...kbData, ownerId }),
      });
      if (!res.ok) throw new Error("Failed to save");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["knowledgeBase", ownerId] }),
  });

  const seedDefaults = () => setKbData((prev) => ({ ...prev, ...YOGAIN_DEFAULTS }));

  const handleSimulate = async () => {
    if (!simInput.trim()) return;
    const userMsg = simInput;
    setSimMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setSimInput("");
    try {
      const res = await apiFetch("/api/whatsapp/simulate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: userMsg, ownerId, knowledgeBase: kbData }),
      });
      const data = await res.json();
      setSimMessages((prev) => [...prev, { role: "ai", text: data.aiReply || "No response. Save your KB first." }]);
    } catch {
      setSimMessages((prev) => [...prev, { role: "ai", text: "Server error." }]);
    }
  };

  const field = (key: keyof KBData) => ({
    value: kbData[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setKbData({ ...kbData, [key]: e.target.value }),
  });

  const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20 transition-all text-sm";
  const textareaCls = `${inputCls} resize-none`;

  return (
    <div className="flex h-full">
      <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
        <div className="max-w-3xl mx-auto space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <BrainCircuit className="w-7 h-7 text-green-600" />
                AI Knowledge Base
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Train the AI on your app, instructors, pricing, and FAQs.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {!remoteKb && (
                <button onClick={seedDefaults} className="px-4 py-2 border border-indigo-200 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Seed YogAIn Defaults
                </button>
              )}
              <button
                onClick={() => setIsSimulating(!isSimulating)}
                className={`px-4 py-2 border rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${isSimulating ? "bg-indigo-100 text-indigo-700 border-indigo-200" : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"}`}
              >
                <MessageCircle className="w-4 h-4" />
                Simulator
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !ownerId}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2 shadow-sm disabled:opacity-50"
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saveMutation.isPending ? "Saving..." : "Save KB"}
              </button>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">
              The AI uses this knowledge base to answer lead questions on WhatsApp and during calls. The <strong>iOS</strong> and <strong>Android</strong> links are sent automatically when a new lead messages — device is asked first, then the right link is sent.
            </p>
          </div>

          {isLoading ? (
            <div className="p-12 text-center text-slate-500 bg-white rounded-xl border">Loading...</div>
          ) : (
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="flex overflow-x-auto border-b bg-slate-50">
                {sections.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setActiveSection(s.key)}
                    className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-all ${activeSection === s.key ? "border-green-500 text-green-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100"}`}
                  >
                    <s.icon className={`w-3.5 h-3.5 ${activeSection === s.key ? s.color : ""}`} />
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="p-6 space-y-5">
                {activeSection === "general" && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">Organization Name</label>
                      <input className={inputCls} placeholder="e.g. YogAIn Wellness" {...field("organizationName")} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">Business Description</label>
                      <textarea className={textareaCls} rows={5} placeholder="YogAIn is an AI-powered yoga wellness app..." {...field("businessDescription")} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">Trial Duration (days)</label>
                      <input
                        type="number" min={1} max={30}
                        className={`${inputCls} w-24`}
                        value={kbData.trialDays}
                        onChange={(e) => setKbData({ ...kbData, trialDays: Number(e.target.value) })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">Qualification Questions</label>
                      <textarea className={textareaCls} rows={5} placeholder="1. Have you downloaded the app?..." {...field("qualificationQuestions")} />
                    </div>
                  </>
                )}

                {activeSection === "app" && (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800">
                      <strong>Auto-send flow:</strong> When a new lead messages, the bot asks which device they use, then sends the matching link below automatically.
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                        <Apple className="w-4 h-4 text-slate-600" />
                        iOS App Store Link
                      </label>
                      <p className="text-xs text-slate-500 mb-2">Sent when the lead selects "iPhone / iPad".</p>
                      <input className={inputCls} placeholder="https://apps.apple.com/app/yogain/id..." {...field("iosAppLink")} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-slate-600" />
                        Android Play Store Link
                      </label>
                      <p className="text-xs text-slate-500 mb-2">Sent when the lead selects "Android".</p>
                      <input className={inputCls} placeholder="https://play.google.com/store/apps/details?id=..." {...field("androidAppLink")} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                        <Link className="w-4 h-4 text-green-600" />
                        Generic App Download Link (fallback)
                      </label>
                      <p className="text-xs text-slate-500 mb-2">Used if the platform-specific link above is empty.</p>
                      <input className={inputCls} placeholder="https://yogainwellness.com/download" {...field("appDownloadLink")} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-emerald-600" />
                        Payment / Subscription Link
                      </label>
                      <p className="text-xs text-slate-500 mb-2">Sent in the package pitch follow-up (30 min after app link).</p>
                      <input className={inputCls} placeholder="https://razorpay.com/payment-link/..." {...field("paymentLink")} />
                    </div>
                  </>
                )}

                {activeSection === "instructors" && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Instructor Profiles</label>
                    <textarea className={textareaCls} rows={10} placeholder={`Instructor 1: Priya Sharma\nStyle: Hatha & Vinyasa\nExperience: 8 years\n...`} {...field("instructorProfiles")} />
                  </div>
                )}

                {activeSection === "timings" && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Class Timings & Schedule</label>
                    <textarea className={textareaCls} rows={10} placeholder={`Morning batch: 7:00 AM – 8:00 AM (Mon–Sat)\nEvening batch: 7:00 PM – 8:00 PM (Mon–Sun)`} {...field("classTimings")} />
                  </div>
                )}

                {activeSection === "streak" && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Streak System</label>
                    <textarea className={textareaCls} rows={8} placeholder="Streak = consecutive days of practice..." {...field("streakSystemInfo")} />
                  </div>
                )}

                {activeSection === "pricing" && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Pricing Plans</label>
                    <p className="text-xs text-slate-500 mb-2">This is included in the package pitch sent automatically 30 min after the app download link.</p>
                    <textarea className={textareaCls} rows={10} placeholder={`Monthly Plan: ₹499/month\n- Unlimited on-demand classes\n...\n\nAnnual Plan: ₹2,999/year ← Best Value`} {...field("pricingPlans")} />
                  </div>
                )}

                {activeSection === "faq" && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">FAQ Answers</label>
                    <textarea className={textareaCls} rows={14} placeholder={`Q: How do I download the app?\nA: We'll send you the link right away!\n\nQ: What is a streak?\nA: ...`} {...field("faqAnswers")} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isSimulating && (
        <div className="w-80 border-l bg-[#efeae2] flex flex-col shrink-0">
          <div className="h-16 bg-white border-b px-4 flex items-center justify-between shrink-0">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              AI Simulator
            </h3>
            <button onClick={() => setIsSimulating(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100">✕</button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {simMessages.length === 0 && (
              <div className="text-center text-xs text-slate-500 mt-8 space-y-2">
                <p>Test how the AI answers lead questions.</p>
                <p className="text-slate-400">Try: "price kya hai?", "streak kya hota hai?", "class timings?"</p>
              </div>
            )}
            {simMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 shadow-sm text-sm ${msg.role === "user" ? "bg-[#d9fdd3] rounded-tr-sm" : "bg-white rounded-tl-sm"}`}>
                  {msg.role === "ai" && <span className="text-[9px] text-purple-600 font-medium block mb-0.5">AI</span>}
                  <p className="text-slate-800 break-words whitespace-pre-wrap">{msg.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 bg-white border-t flex gap-2">
            <input
              type="text" placeholder="Ask as a lead..."
              value={simInput}
              onChange={(e) => setSimInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSimulate()}
              className="flex-1 bg-slate-100 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-500/20"
            />
            <button onClick={handleSimulate} disabled={!simInput.trim()} className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 disabled:opacity-50">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
